import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Scalar helpers
 */
const UUIDSchema = z.uuid();
const DecimalStringSchema = z.string().regex(/^-?\d+(\.\d+)?$/, {
  error: "Must be a numeric string",
});

/**
 * EDI844 Submission Status enum (matching backend model)
 */
export const EDI844SubmissionStatusSchema = z.enum([
  "draft",
  "sent",
  "pending",
  "disputed",
  "accepted",
  "rejected",
  "partial",
]);

/**
 * EDI844 Delivery Method enum (matching backend model)
 */
export const EDI844DeliveryMethodSchema = z.enum(["edi", "email"]);

/**
 * EDI844 Submission Line Status enum (matching backend model)
 */
export const EDI844SubmissionLineStatusSchema = z.enum([
  "pending",
  "accepted",
  "rejected",
  "partial",
  "disputed",
]);

/**
 * EDI844 Submission Activity Type enum (matching backend model)
 */
export const EDI844SubmissionActivityTypeSchema = z.enum([
  "created",
  "sent_via_edi",
  "sent_via_email",
  "response_received",
  "line_accepted",
  "line_rejected",
  "line_partial",
  "disputed",
  "dispute_response",
  "comment_added",
  "status_changed",
  "vdm_created",
  "error_occurred",
]);

/**
 * Pagination schema
 */
export const EDI844PaginationSchema = z.object({
  total: z.int().nonnegative(),
  limit: z.int().positive(),
  offset: z.int().nonnegative(),
});

/**
 * Supplier summary for submission response
 */
const SubmissionSupplierSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
});

/**
 * User summary for activity response
 */
const ActivityUserSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
});

/**
 * Source transaction summary (from RexelE844sdFile)
 */
const SourceTransactionSchema = z.object({
  id: UUIDSchema,
  invoiceNumber: z.string().nullable().optional(),
  customerPoNumber: z.string().nullable().optional(),
  itemInternalCode: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  shippedQuantity: z.coerce.number().nullable().optional(),
  claimAmount: z.coerce.number().nullable().optional(),
  tradingPartner: z.string().nullable().optional(),
  agreementNumber: z.string().nullable().optional(),
});

/**
 * EDI844 Submission Line schema
 */
export const EDI844SubmissionLineSchema = z.object({
  id: UUIDSchema,
  submissionId: UUIDSchema,
  rexelE844sdFileId: UUIDSchema,
  lineStatus: EDI844SubmissionLineStatusSchema,
  claimedAmount: DecimalStringSchema,
  acceptedAmount: DecimalStringSchema.nullable().optional(),
  rejectionReason: z.string().nullable().optional(),
  responseMetadata: z.record(z.string(), z.unknown()).nullable().optional(),
  sourceTransaction: SourceTransactionSchema.optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/**
 * EDI844 Submission schema for RPC responses
 */
export const EDI844SubmissionSchema = z.object({
  id: UUIDSchema,
  organizationId: UUIDSchema,
  supplierId: UUIDSchema,
  supplier: SubmissionSupplierSchema.optional(),
  companyCode: z.string(),
  divisionCode: z.string(),
  sellingLocationCode: z.string(),
  sellingLocationName: z.string().nullable().optional(),
  submissionNumber: z.int(),
  status: EDI844SubmissionStatusSchema,
  deliveryMethod: EDI844DeliveryMethodSchema,
  totalClaimedAmount: DecimalStringSchema,
  totalAcceptedAmount: DecimalStringSchema.nullable().optional(),
  submissionDate: z.coerce.date(),
  sentAt: z.coerce.date().nullable().optional(),
  externalReference: z.string().nullable().optional(),
  outboundEmailId: UUIDSchema.nullable().optional(),
  vdmId: UUIDSchema.nullable().optional(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  lineCount: z.int().optional(),
  lines: z.array(EDI844SubmissionLineSchema).optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable().optional(),
});

/**
 * List Submissions input schema with filters
 */
export const ListEDI844SubmissionsInputSchema = z.object({
  // Filter by status
  status: z.array(EDI844SubmissionStatusSchema).optional(),
  // Filter by supplier
  supplierId: UUIDSchema.optional(),
  // Filter by division code
  divisionCode: z.string().optional(),
  // Filter by selling location
  sellingLocationCode: z.string().optional(),
  // Filter by delivery method
  deliveryMethod: EDI844DeliveryMethodSchema.optional(),
  // Filter by submission number
  submissionNumber: z.int().optional(),
  // Date filters
  submissionDateFrom: z.string().optional(),
  submissionDateTo: z.string().optional(),
  sentAtFrom: z.string().optional(),
  sentAtTo: z.string().optional(),
  // Include relations
  includeLines: z.boolean().optional(),
  includeSupplier: z.boolean().optional(),
  // Pagination
  limit: z.int().min(1).max(100).optional(),
  offset: z.int().min(0).optional(),
});

/**
 * List Submissions response schema
 */
export const ListEDI844SubmissionsResponseSchema = z.object({
  submissions: z.array(EDI844SubmissionSchema),
  pagination: EDI844PaginationSchema,
});

/**
 * Get single Submission input schema
 */
export const GetEDI844SubmissionInputSchema = z.object({
  id: UUIDSchema,
  includeLines: z.boolean().optional(),
  includeSupplier: z.boolean().optional(),
  includeLineSourceTransactions: z.boolean().optional(),
});

/**
 * Get Submission response schema
 */
export const GetEDI844SubmissionResponseSchema = EDI844SubmissionSchema;

/**
 * Create Submission input schema
 */
export const CreateEDI844SubmissionInputSchema = z.object({
  supplierId: UUIDSchema,
  companyCode: z.string(),
  divisionCode: z.string(),
  sellingLocationCode: z.string(),
  sellingLocationName: z.string().optional(),
  deliveryMethod: EDI844DeliveryMethodSchema.optional(),
  submissionDate: z.coerce.date().optional(),
  externalReference: z.string().optional(),
  // IDs of RexelE844sdFile records to include in this submission
  transactionIds: z.array(UUIDSchema).min(1),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

/**
 * Create Submission response schema
 */
export const CreateEDI844SubmissionResponseSchema = EDI844SubmissionSchema;

/**
 * Update Submission input schema
 */
export const UpdateEDI844SubmissionInputSchema = z.object({
  id: UUIDSchema,
  status: EDI844SubmissionStatusSchema.optional(),
  deliveryMethod: EDI844DeliveryMethodSchema.optional(),
  externalReference: z.string().nullable().optional(),
  sentAt: z.coerce.date().nullable().optional(),
  outboundEmailId: UUIDSchema.nullable().optional(),
  vdmId: UUIDSchema.nullable().optional(),
  totalAcceptedAmount: DecimalStringSchema.optional(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
});

/**
 * Update Submission response schema
 */
export const UpdateEDI844SubmissionResponseSchema = EDI844SubmissionSchema;

/**
 * Update Submission Line input schema
 */
export const UpdateEDI844SubmissionLineInputSchema = z.object({
  id: UUIDSchema,
  lineStatus: EDI844SubmissionLineStatusSchema.optional(),
  acceptedAmount: DecimalStringSchema.nullable().optional(),
  rejectionReason: z.string().nullable().optional(),
  responseMetadata: z.record(z.string(), z.unknown()).nullable().optional(),
});

/**
 * Update Submission Line response schema
 */
export const UpdateEDI844SubmissionLineResponseSchema =
  EDI844SubmissionLineSchema;

/**
 * Batch Update Lines input schema (for processing 849 responses)
 */
export const BatchUpdateEDI844SubmissionLinesInputSchema = z.object({
  submissionId: UUIDSchema,
  lines: z.array(
    z.object({
      id: UUIDSchema,
      lineStatus: EDI844SubmissionLineStatusSchema,
      acceptedAmount: DecimalStringSchema.nullable().optional(),
      rejectionReason: z.string().nullable().optional(),
      responseMetadata: z.record(z.string(), z.unknown()).nullable().optional(),
    }),
  ),
  // Optional: automatically calculate and update submission totals
  recalculateTotals: z.boolean().optional(),
});

/**
 * Batch Update Lines response schema
 */
export const BatchUpdateEDI844SubmissionLinesResponseSchema = z.object({
  submission: EDI844SubmissionSchema,
  updatedLines: z.array(EDI844SubmissionLineSchema),
});

/**
 * EDI844 Submission Activity schema for RPC responses
 */
export const EDI844SubmissionActivitySchema = z.object({
  id: UUIDSchema,
  submissionId: UUIDSchema,
  submissionLineId: UUIDSchema.nullable().optional(),
  activityType: EDI844SubmissionActivityTypeSchema,
  userId: UUIDSchema.nullable().optional(),
  user: ActivityUserSchema.nullable().optional(),
  inboundEmailId: UUIDSchema.nullable().optional(),
  ediDocumentType: z.string().nullable().optional(),
  eventAt: z.coerce.date(),
  description: z.string(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  createdAt: z.coerce.date(),
});

/**
 * List Submission Activities input schema
 */
export const ListEDI844SubmissionActivitiesInputSchema = z.object({
  submissionId: UUIDSchema,
  // Filter by activity type
  activityType: z.array(EDI844SubmissionActivityTypeSchema).optional(),
  // Filter by line (null = submission-level activities only)
  submissionLineId: UUIDSchema.nullable().optional(),
  // Pagination
  limit: z.int().min(1).max(100).optional(),
  offset: z.int().min(0).optional(),
});

/**
 * List Submission Activities response schema
 */
export const ListEDI844SubmissionActivitiesResponseSchema = z.object({
  activities: z.array(EDI844SubmissionActivitySchema),
  pagination: EDI844PaginationSchema,
});

/**
 * Add Comment input schema
 */
export const AddEDI844SubmissionCommentInputSchema = z.object({
  submissionId: UUIDSchema,
  submissionLineId: UUIDSchema.optional(),
  comment: z.string().min(1).max(2000),
});

/**
 * Add Comment response schema
 */
export const AddEDI844SubmissionCommentResponseSchema =
  EDI844SubmissionActivitySchema;

/**
 * Log Activity input schema (for system events)
 */
export const LogEDI844SubmissionActivityInputSchema = z.object({
  submissionId: UUIDSchema,
  submissionLineId: UUIDSchema.optional(),
  activityType: EDI844SubmissionActivityTypeSchema,
  description: z.string(),
  inboundEmailId: UUIDSchema.optional(),
  ediDocumentType: z.string().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

/**
 * Log Activity response schema
 */
export const LogEDI844SubmissionActivityResponseSchema =
  EDI844SubmissionActivitySchema;

/**
 * Group pending transactions by batch key (supplier + division + selling location)
 */
export const GetEDI844BatchGroupsInputSchema = z.object({
  // Optional filters
  supplierId: UUIDSchema.optional(),
  divisionCode: z.string().optional(),
  sellingLocationCode: z.string().optional(),
  // Only include transactions not yet in a submission
  excludeSubmitted: z.boolean().optional(),
});

/**
 * Batch group summary
 */
const BatchGroupSchema = z.object({
  supplierId: UUIDSchema,
  supplierName: z.string(),
  divisionCode: z.string(),
  sellingLocationCode: z.string(),
  sellingLocationName: z.string().nullable().optional(),
  transactionCount: z.int(),
  totalClaimAmount: z.number(),
  transactionIds: z.array(UUIDSchema),
});

/**
 * Get Batch Groups response schema
 */
export const GetEDI844BatchGroupsResponseSchema = z.object({
  groups: z.array(BatchGroupSchema),
});

/**
 * Send Workbench Transactions input schema
 */
export const SendWorkbenchTransactionsInputSchema = z.object({
  transactionIds: z.array(UUIDSchema).min(1),
});

/**
 * Submission info returned for each division batch
 */
const WorkbenchSubmissionInfoSchema = z.object({
  id: UUIDSchema,
  division: z.string(),
  batchNumber: z.string().nullable(),
  status: EDI844SubmissionStatusSchema,
  transactionCount: z.int(),
  totalClaimedAmount: z.string(),
});

/**
 * Failed transaction info
 */
const WorkbenchFailedTransactionSchema = z.object({
  id: UUIDSchema,
  reason: z.string(),
});

/**
 * Send Workbench Transactions response schema
 */
export const SendWorkbenchTransactionsResponseSchema = z.object({
  successful: z.array(UUIDSchema),
  failed: z.array(WorkbenchFailedTransactionSchema),
  submissions: z.array(WorkbenchSubmissionInfoSchema),
  summary: z.object({
    totalRequested: z.int(),
    successCount: z.int(),
    failedCount: z.int(),
    submissionCount: z.int(),
  }),
});

/**
 * EDI844 Submissions RPC Contracts
 */
export const edi844SubmissionsContracts = {
  // Submission CRUD
  list: oc
    .input(ListEDI844SubmissionsInputSchema)
    .output(ListEDI844SubmissionsResponseSchema)
    .route({
      method: "GET",
    }),
  get: oc
    .input(GetEDI844SubmissionInputSchema)
    .output(GetEDI844SubmissionResponseSchema)
    .route({
      method: "GET",
    }),
  create: oc
    .input(CreateEDI844SubmissionInputSchema)
    .output(CreateEDI844SubmissionResponseSchema)
    .route({
      method: "POST",
    }),
  update: oc
    .input(UpdateEDI844SubmissionInputSchema)
    .output(UpdateEDI844SubmissionResponseSchema)
    .route({
      method: "PATCH",
    }),

  // Line operations
  updateLine: oc
    .input(UpdateEDI844SubmissionLineInputSchema)
    .output(UpdateEDI844SubmissionLineResponseSchema)
    .route({
      method: "PATCH",
    }),
  batchUpdateLines: oc
    .input(BatchUpdateEDI844SubmissionLinesInputSchema)
    .output(BatchUpdateEDI844SubmissionLinesResponseSchema)
    .route({
      method: "POST",
    }),

  // Activity/timeline
  listActivities: oc
    .input(ListEDI844SubmissionActivitiesInputSchema)
    .output(ListEDI844SubmissionActivitiesResponseSchema)
    .route({
      method: "GET",
    }),
  addComment: oc
    .input(AddEDI844SubmissionCommentInputSchema)
    .output(AddEDI844SubmissionCommentResponseSchema)
    .route({
      method: "POST",
    }),
  logActivity: oc
    .input(LogEDI844SubmissionActivityInputSchema)
    .output(LogEDI844SubmissionActivityResponseSchema)
    .route({
      method: "POST",
    }),

  // Batch grouping helper
  getBatchGroups: oc
    .input(GetEDI844BatchGroupsInputSchema)
    .output(GetEDI844BatchGroupsResponseSchema)
    .route({
      method: "GET",
    }),

  // Workbench send
  sendWorkbenchTransactions: oc
    .input(SendWorkbenchTransactionsInputSchema)
    .output(SendWorkbenchTransactionsResponseSchema)
    .route({
      method: "POST",
    }),
} as const;

// Type exports
export type EDI844SubmissionStatus = z.infer<
  typeof EDI844SubmissionStatusSchema
>;
export type EDI844DeliveryMethod = z.infer<typeof EDI844DeliveryMethodSchema>;
export type EDI844SubmissionLineStatus = z.infer<
  typeof EDI844SubmissionLineStatusSchema
>;
export type EDI844SubmissionActivityType = z.infer<
  typeof EDI844SubmissionActivityTypeSchema
>;
export type EDI844SubmissionLine = z.infer<typeof EDI844SubmissionLineSchema>;
export type EDI844Submission = z.infer<typeof EDI844SubmissionSchema>;
export type ListEDI844SubmissionsInput = z.infer<
  typeof ListEDI844SubmissionsInputSchema
>;
export type ListEDI844SubmissionsResponse = z.infer<
  typeof ListEDI844SubmissionsResponseSchema
>;
export type GetEDI844SubmissionInput = z.infer<
  typeof GetEDI844SubmissionInputSchema
>;
export type GetEDI844SubmissionResponse = z.infer<
  typeof GetEDI844SubmissionResponseSchema
>;
export type CreateEDI844SubmissionInput = z.infer<
  typeof CreateEDI844SubmissionInputSchema
>;
export type CreateEDI844SubmissionResponse = z.infer<
  typeof CreateEDI844SubmissionResponseSchema
>;
export type UpdateEDI844SubmissionInput = z.infer<
  typeof UpdateEDI844SubmissionInputSchema
>;
export type UpdateEDI844SubmissionResponse = z.infer<
  typeof UpdateEDI844SubmissionResponseSchema
>;
export type UpdateEDI844SubmissionLineInput = z.infer<
  typeof UpdateEDI844SubmissionLineInputSchema
>;
export type UpdateEDI844SubmissionLineResponse = z.infer<
  typeof UpdateEDI844SubmissionLineResponseSchema
>;
export type BatchUpdateEDI844SubmissionLinesInput = z.infer<
  typeof BatchUpdateEDI844SubmissionLinesInputSchema
>;
export type BatchUpdateEDI844SubmissionLinesResponse = z.infer<
  typeof BatchUpdateEDI844SubmissionLinesResponseSchema
>;
export type EDI844SubmissionActivity = z.infer<
  typeof EDI844SubmissionActivitySchema
>;
export type ListEDI844SubmissionActivitiesInput = z.infer<
  typeof ListEDI844SubmissionActivitiesInputSchema
>;
export type ListEDI844SubmissionActivitiesResponse = z.infer<
  typeof ListEDI844SubmissionActivitiesResponseSchema
>;
export type AddEDI844SubmissionCommentInput = z.infer<
  typeof AddEDI844SubmissionCommentInputSchema
>;
export type AddEDI844SubmissionCommentResponse = z.infer<
  typeof AddEDI844SubmissionCommentResponseSchema
>;
export type LogEDI844SubmissionActivityInput = z.infer<
  typeof LogEDI844SubmissionActivityInputSchema
>;
export type LogEDI844SubmissionActivityResponse = z.infer<
  typeof LogEDI844SubmissionActivityResponseSchema
>;
export type GetEDI844BatchGroupsInput = z.infer<
  typeof GetEDI844BatchGroupsInputSchema
>;
export type GetEDI844BatchGroupsResponse = z.infer<
  typeof GetEDI844BatchGroupsResponseSchema
>;
export type SendWorkbenchTransactionsInput = z.infer<
  typeof SendWorkbenchTransactionsInputSchema
>;
export type SendWorkbenchTransactionsResponse = z.infer<
  typeof SendWorkbenchTransactionsResponseSchema
>;
